<script lang="ts" setup>
import type { CarsListCar, SearchListCar } from '@autobid/ui/types/Car'
import { useSwipe } from '@autobid/ui/composables/useSwipe'
import { injectFilterComposable } from '@autobid/ui/utils/carsList/injectFilterComposable'
import { computed, ref, watch } from 'vue'

interface Props {
  cars: CarsListCar[] | SearchListCar[]
  pages: number
  isFetching: boolean
}

const props = defineProps<Props>()

const useFilter = injectFilterComposable()
const {
  filters: { currentPage, pageType },
} = useFilter()
const componentWrapperNode = ref<HTMLDivElement>()
const currentValue = computed(() => {
  const value = Number(currentPage.value)

  return Number.isNaN(value) ? 1 : value
})

function scrollToTopOfList() {
  if (!process.client || !componentWrapperNode.value) {
    return
  }

  const elementPosition = componentWrapperNode.value.getBoundingClientRect().top
  const ADDITIONAL_OFFSET = 100 // for a better UX
  const offsetPosition = elementPosition + window.scrollY - ADDITIONAL_OFFSET

  window.scrollTo({
    top: window.scrollY < offsetPosition ? window.scrollY : offsetPosition,
  })
}
watch(currentPage, scrollToTopOfList)

const ANIMATION_DURATION = 400
const isAnimating = ref(false)
const isPaginating = ref(false)
const animationType = ref<'slide-left' | 'slide-right'>()
useSwipe({
  onSwipe: (direction) => {
    if (direction === 'left' && currentValue.value < props.pages) {
      changePage(currentValue.value + 1)
    }
    else if (direction === 'right' && currentValue.value > 1) {
      changePage(currentValue.value - 1)
    }
  },
})

function changePage(page: number) {
  isAnimating.value = true
  isPaginating.value = true
  setTimeout(() => {
    isAnimating.value = false
  }, ANIMATION_DURATION)

  animationType.value = currentValue.value > page ? 'slide-right' : 'slide-left'

  currentPage.value = page
  pageType.value = currentValue.value > page ? 'prev' : 'next'
}

watch(
  () => props.cars,
  () => {
    isPaginating.value = false

    setTimeout(() => {
      animationType.value = undefined
    }, ANIMATION_DURATION)
  },
)
</script>

<template>
  <div v-if="cars" ref="componentWrapperNode" class="container mx-auto md:mt-6">
    <slot name="beforeAll" />

    <div class="flex">
      <slot name="leftSide" />

      <div class="cars-list relative mb-10 w-full overflow-hidden px-3">
        <template v-if="cars.length">
          <slot name="aboveCars" />

          <div class="w-full">
            <div
              class="w-full"
              :class="{ 'opacity-50': isFetching && !isPaginating }"
            >
              <div
                class="relative z-10"
                :style="
                  isFetching && !isAnimating
                    ? `min-height: ${208 * cars.length}px`
                    : ''
                "
              >
                <transition
                  :name="animationType"
                  mode="out-in"
                  :duration="ANIMATION_DURATION"
                >
                  <div
                    v-if="!isFetching && !isPaginating"
                    class="relative z-10 w-full"
                  >
                    <DynamicAuctionItemListCar
                      v-for="(car, index) in cars"
                      :key="car.id"
                      :is-first="index === 0"
                      :car="car"
                    />
                  </div>
                </transition>

                <div
                  v-if="isFetching && !isAnimating && isPaginating"
                  class="absolute left-0 top-0 z-20 w-full"
                >
                  <div
                    v-for="car in cars"
                    :key="car.id"
                    class="my-4 h-48 w-full animate-pulse bg-gray-200"
                  />
                </div>
              </div>
            </div>

            <CommonPagination
              v-if="pages"
              :current-page="currentValue"
              :pages="pages"
              class="fixed bottom-0 left-0 z-40 mt-10 w-full bg-white py-3 md:static"
              @paginate="changePage"
            />
          </div>
        </template>
        <p v-else-if="!isFetching">
          {{ $t('cars-list.no-cars') }}
        </p>
      </div>
    </div>
  </div>
</template>
